import React, { useEffect, useRef, useState } from 'react';
import { api, buildAssetUrl, mensajeError } from '../lib/api';
import { PageHeader, SectionCard } from '../components/Common';
import ConectorMcpPanel from '../components/ConectorMcpPanel';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Save, Upload, Loader2, Link2 } from 'lucide-react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { useAuth } from '../lib/auth';
import { isAdmin, isGestion, isManagement } from '../lib/roles';
import CatalogosPendientes from '../components/CatalogosPendientes';
import MarcasPanel from '../components/MarcasPanel';
import IpsClinicaPanel from '../components/settings/IpsClinicaPanel';
import MarcasPacientePanel from '../components/settings/MarcasPacientePanel';
import {
  AjustesPagosResenas, AjustesPlantillasWhatsApp, AjustesRecetas, FaltanDatosClinica,
} from '../components/settings/DatosDeLaClinica';
import MarcaDocumentosAjuste from '../components/settings/MarcaDocumentosAjuste';

function ImageUpload({ label, currentFileId, kind, onUploaded, soloLectura = false }) {
  // `id` = «ajuste-<campo>»: la lista de lo que falta lleva aquí (V2).
  const inputRef = useRef();
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const res = await api.post(`/api/settings/upload/${kind}`, fd);
      toast.success(`${label} actualizado`);
      onUploaded && onUploaded(res.data.file_id);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Error'); } finally { setBusy(false); if (inputRef.current) inputRef.current.value = ''; }
  };
  return (
    <div className="space-y-2" id={`ajuste-${kind}_path`}>
      <Label>{label}</Label>
      <div className="flex items-center gap-4">
        <div className="h-20 w-20 rounded-lg border border-dashed border-border bg-[rgba(46,110,94,0.03)] flex items-center justify-center overflow-hidden">
          {currentFileId ? <img src={buildAssetUrl(currentFileId)} alt={label} className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-muted-foreground text-center">Sin imagen</span>}
        </div>
        {!soloLectura && (
          <>
            <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onFile} data-testid={`settings-upload-${kind}-input`} />
            <Button type="button" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} data-testid={`settings-upload-${kind}-button`}>
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Subiendo…</> : <><Upload className="w-4 h-4 mr-2" /> Subir</>}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  // Quién cambia la configuración (M31, informe del 22/9/2026): el servidor
  // solo deja guardar y subir a dirección (`require_management`: admin,
  // dirección y sus roles viejos); a administrativo, enfermería y anestesista
  // «Guardar cambios» y «Subir» les daban un 403. Ellas siguen entrando —aquí
  // están las comprobaciones diarias, que son de todo el equipo— y ven lo que
  // hay, pero sin botones que el servidor les va a rechazar.
  const { user } = useAuth();
  const soloLectura = !isManagement(user);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  // La puerta de al lado de M31: si la carga fallaba, la pantalla salía con
  // los campos en blanco, como si la clínica no tuviera nombre ni teléfono
  // (y el admin podía guardar encima). Un error se dice; no se pinta como dato.
  const [errorCarga, setErrorCarga] = useState('');
  // Datos de la clínica que faltan (V2, 28/9/2026): lo pide aparte para que
  // no viaje en el formulario (el PUT guardaría el eco).
  const [faltan, setFaltan] = useState([]);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/api/settings');
      setSettings(r.data); setForm(r.data || {}); setErrorCarga('');
      api.get('/api/settings/faltan')
        .then((f) => setFaltan(Array.isArray(f.data?.faltan) ? f.data.faltan : []))
        .catch(() => {});
    } catch (err) {
      setErrorCarga(mensajeError(err, 'No se ha podido cargar la configuración.'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put('/api/settings', form);
      setSettings(r.data); toast.success('Configuración guardada');
      api.get('/api/settings/faltan')
        .then((f) => setFaltan(Array.isArray(f.data?.faltan) ? f.data.faltan : []))
        .catch(() => {});
    } catch (err) { toast.error(mensajeError(err, 'No se ha podido guardar la configuración.')); } finally { setBusy(false); }
  };
  if (loading) return <div className="py-10 text-center text-sm text-muted-foreground">Cargando…</div>;
  if (errorCarga) {
    return (
      <div>
        <PageHeader title="Configuración" subtitle="Identidad de la clínica, logos, firma y sello del doctor." />
        <div className="cjo-card p-6 text-sm text-red-800 bg-red-50 border border-red-200 rounded-lg" data-testid="settings-error-carga">
          <p>{errorCarga}</p>
          <Button variant="outline" className="mt-3" onClick={load} data-testid="settings-reintentar">Reintentar</Button>
        </div>
      </div>
    );
  }
  return (
    <div>
      <PageHeader title="Configuración"
        subtitle={soloLectura
          ? 'Identidad de la clínica, logos, firma y sello del doctor. Solo dirección puede cambiarlos.'
          : 'Identidad de la clínica, logos, firma y sello del doctor.'}
        actions={soloLectura ? null : <Button onClick={save} disabled={busy} className="bg-[#2E6E5E] hover:bg-[#1E4A3F]" data-testid="settings-save-button"><Save className="w-4 h-4 mr-2" /> {busy ? 'Guardando…' : 'Guardar cambios'}</Button>} />
      <FaltanDatosClinica faltan={faltan} />
      {/* Los catálogos de la clínica que viven en la base y faltan (V2). */}
      <CatalogosPendientes />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SectionCard title="Datos de la clínica">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div className="md:col-span-2"><Label htmlFor="ajuste-clinic_name">Nombre clínica</Label><Input id="ajuste-clinic_name" disabled={soloLectura} value={form.clinic_name || ''} onChange={(e) => setF('clinic_name', e.target.value)} /></div>
            <div><Label htmlFor="ajuste-legal_entity">Razón social</Label><Input id="ajuste-legal_entity" disabled={soloLectura} value={form.legal_entity || ''} onChange={(e) => setF('legal_entity', e.target.value)} /></div>
            <div><Label htmlFor="ajuste-phone">Teléfono</Label><Input id="ajuste-phone" disabled={soloLectura} value={form.phone || ''} onChange={(e) => setF('phone', e.target.value)} /></div>
            <div className="md:col-span-2"><Label htmlFor="ajuste-address">Dirección</Label><Input id="ajuste-address" disabled={soloLectura} value={form.address || ''} onChange={(e) => setF('address', e.target.value)} /></div>
            <div><Label htmlFor="ajuste-email">Email</Label><Input id="ajuste-email" disabled={soloLectura} value={form.email || ''} onChange={(e) => setF('email', e.target.value)} /></div>
            <div><Label htmlFor="ajuste-website">Web</Label><Input id="ajuste-website" disabled={soloLectura} value={form.website || ''} onChange={(e) => setF('website', e.target.value)} /></div>
            <div><Label htmlFor="ajuste-doctor_name">Doctor</Label><Input id="ajuste-doctor_name" disabled={soloLectura} value={form.doctor_name || ''} onChange={(e) => setF('doctor_name', e.target.value)} /></div>
            <div><Label htmlFor="ajuste-doctor_license">Nº colegiado</Label><Input id="ajuste-doctor_license" disabled={soloLectura} value={form.doctor_license || ''} onChange={(e) => setF('doctor_license', e.target.value)} /></div>
          </div>
        </SectionCard>
        <SectionCard title="Logos">
          <div className="space-y-5">
            <ImageUpload label="Logo claro (sobre fondo blanco)" kind="logo_light" currentFileId={settings?.logo_light_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <ImageUpload label="Logo oscuro (sobre fondo azul)" kind="logo_dark" currentFileId={settings?.logo_dark_path} onUploaded={() => load()} soloLectura={soloLectura} />
            {/* Logos de los buzones de la bandeja de correo (V2, 28/9/2026:
                iban empaquetados en el frontend). Sin logo, sus iniciales. */}
            <ImageUpload label="Logo del buzón principal" kind="logo_buzon_clinica" currentFileId={settings?.logo_buzon_clinica_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <ImageUpload label="Logo del segundo buzón" kind="logo_buzon_instituto" currentFileId={settings?.logo_buzon_instituto_path} onUploaded={() => load()} soloLectura={soloLectura} />
          </div>
        </SectionCard>
        <AjustesPagosResenas form={form} setF={setF} soloLectura={soloLectura} />
        <AjustesRecetas form={form} setF={setF} soloLectura={soloLectura} />
        <AjustesPlantillasWhatsApp form={form} setF={setF} soloLectura={soloLectura} />
        {/* La marca de los documentos de los médicos que no tienen una propia
            (20/10/2026): se guarda con «Guardar cambios», como lo demás. */}
        <MarcaDocumentosAjuste form={form} setF={setF} soloLectura={!isAdmin(user)} />
        {/* Comprobaciones diarias (2026-09-06 los enlaces del paciente,
            2026-09-10 el día de consulta de recepción): el estado vive en su
            propia pantalla, en dos pestañas; desde aquí se llega. */}
        <SectionCard title="Comprobaciones diarias">
          <p className="text-sm text-muted-foreground mb-3">
            Cada día laborable se comprueba, como paciente, que todos los enlaces que
            mandamos (confirmar cita, documento único, cuestionario de salud, guía de
            cirugía, paquete online y tablet) funcionan y se pueden rellenar y enviar.
            Y, con los ojos de recepción, que el día de consulta entero se puede hacer:
            agenda, citas, mensajes, cobros, papeles, ficha y quirófano.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" data-testid="settings-salud-enlaces-link">
              <Link to="/salud-enlaces"><Link2 className="w-4 h-4 mr-2" /> Ver el estado de los enlaces</Link>
            </Button>
            <Button asChild variant="outline" data-testid="settings-salud-recepcion-link">
              <Link to="/salud-enlaces?vista=recepcion"><Link2 className="w-4 h-4 mr-2" /> Ver el día de consulta</Link>
            </Button>
          </div>
        </SectionCard>
        <SectionCard title="Firma y sello del Doctor">
          <div className="space-y-5">
            <ImageUpload label="Firma del Doctor" kind="doctor_signature" currentFileId={settings?.doctor_signature_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <ImageUpload label="Sello" kind="doctor_stamp" currentFileId={settings?.doctor_stamp_path} onUploaded={() => load()} soloLectura={soloLectura} />
            <p className="text-xs text-muted-foreground">Estos se incrustarán automáticamente en los consentimientos generados.</p>
          </div>
        </SectionCard>
        <ConectorMcpPanel />
        {/* Las marcas de la clínica (V2): solo admin las cambia. */}
        {isAdmin(user) ? <MarcasPanel /> : null}
        {/* Desde dónde vale la contraseña corta de la tablet (29/9/2026):
            solo admin, como Usuarios. */}
        {isAdmin(user) ? <IpsClinicaPanel /> : null}
        {/* Marcas de paciente (14/10/2026): gestión las configura. */}
        {isGestion(user) ? <MarcasPacientePanel /> : null}
      </div>
    </div>
  );
}
